(ns practitest.frontend.common-entities.index
  (:require
    [practitest.frontend.common-entities :refer [batch-navigate!
                                                 over-batch-limit
                                                 prepare-full-selection-ids]]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.batch-modal.events :as-alias batch-modal-events]
    [practitest.frontend.common-entities.metadata :refer [entities-metadata
                                                          get-entity-name]]
    [practitest.frontend.common.bugtrackers :refer [get-label jira? azure? clickup? youtrack? service-now?]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [format-translation
                                             get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.permissions :as permissions]
    [practitest.frontend.common.re-frame :refer [partial-dispatch]]
    [practitest.frontend.components.grid.server :as server]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.grid.subs :as grid.subs]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.project-assistant.events :as-alias project-assistant.events]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components.view-editor.events :as-alias view-editor.events]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.dropdown :as dropdown]
    [practitest.frontend.forms3.entity-form.fields :refer [go-to-component]]
    [practitest.frontend.components2.grid.save-filter-modal :as save-filter-modal]
    [practitest.frontend.components2.resize-panel :refer [panel-group
                                                          panel-resize-handle
                                                          panel-resizeable]]
    [practitest.frontend.components2.search-box :refer [search-textbox]]
    [practitest.frontend.folders :as folders]
    [practitest.frontend.import-ticket-modal.main :as import-ticket]
    [practitest.frontend.issue.bugtracker :refer [bugtracker-logo]]
    [practitest.frontend.pt-ts-bridge :refer [FiltersPanel]]
    [practitest.frontend.ptql-editor :as ptql-editor]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.tests.batch-set-version-label :as batch-version-label]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(def max-version-label-batch-selection 500)

(defn top-page-title [entity state-key]
  (let [page-title (get-entity-name entity :page-title)
       plural-name (get-entity-name entity :plural-name)
       total-count @(re-frame/subscribe [::grid.subs/entities-total-count entity state-key])
       instance-view-mode? (and (= entity :testset) @(re-frame/subscribe [:practitest.frontend.testset2.subs/instance-view-mode?]))
       current-view @(re-frame/subscribe [::grid.subs/current-view entity state-key])
       filter-name (or (:name current-view) (str "All " plural-name))
       filter-title (str filter-name
                         (when-not (:is_auto_filter_child current-view)
                           (str " (" total-count ")")))
       module-title (if instance-view-mode?
                      (str page-title " - " (get-translation "testset.button.instance-view"  "Instance View"))
                      page-title)]
   [:div.col-6
    [:div.top-page-title
     [:span.module-title module-title]
     [:span.icon-wrapper [icons/mux-grid-filter]]
     [:span.filter-title filter-title]]]))

(defn bugtracker-dropdown-item [bugtracker entity can-create? sync-enabled?]
  (let [label (get-label bugtracker)
        text (format-translation
               (get-translation "entity.dropdown.import_from_bugtracker"
                                (str "Import ticket from " label))
               :bugtracker label)
        click-event #(import-ticket/show-import-from-bugtracker-modal bugtracker entity)
        sync-disabled? (and (or (jira? bugtracker) (azure? bugtracker) (clickup? bugtracker) (youtrack? bugtracker)
                                (service-now? bugtracker))
                            (not sync-enabled?))
        disabled? (or (not can-create?) sync-disabled?)]
    [dropdown/dropdown-menu-item {:text text
                                  :icon [bugtracker-logo]
                                  :on-click click-event
                                  :disabled? disabled?
                                  :tooltip (when sync-disabled?
                                             (get-translation
                                               "entity.dropdown.sync_disabled_tooltip"
                                               "This option has been disabled by your administrator's settings."))}]))

(defn new-entity-dropdown-btn [entity]
  (let [{:keys [urls edit-permissions]} (get entities-metadata entity)
        current-project @(re-frame/subscribe [::subs/current-project])
        bugtracker (:integrated_bugtracker current-project)
        allow_issue_creation (:allow_issue_creation current-project)
        skip-mapping? (:skip_mapping_in_manual_import current-project)
        sync-enabled? (if (not skip-mapping?)
                         (case entity
                           :requirement (:requirements_sync_enabled current-project true)
                           :issue (:issues_sync_enabled current-project true)
                           true)
                         true)
        new-url (broutes/get-url-view (:new urls))
        entity-name (get-entity-name entity :singular-name)
        can-create? (permissions/has-permission? edit-permissions)]
    [dropdown/dropdown-button
     {:text (get-translation
              (str (name entity) ".new")
              (str "New" " " entity-name))
      :title (get-translation
              (str (name entity) ".new")
              (str "New" " " entity-name))
      :primary? true
      :disabled? (not can-create?)
      :class "hide-text"
      :icon [icons/mux-plus-circle]}
     {:placement "bottom-end"}
     [dropdown/dropdown-menu-item {:icon [icons/mux-plus-circle]
                                   :href new-url
                                   :text (get-translation (str (name entity) ".dropdown.create_new")
                                                          (str "Create new " entity-name))
                                   :mixpanel  ["Grid Actions" {"Action" (str "Create new " entity-name)}]
                                   :disabled? (or (not can-create?)
                                                  (and (not allow_issue_creation) (= entity :issue)))}]
    [bugtracker-dropdown-item bugtracker entity can-create? sync-enabled?]]))

(defn new-entity-button-data [entity]
  (let [{:keys [urls new-ui-form? edit-permissions]} (get entities-metadata entity)
        entity-name (get-entity-name entity :singular-name)
        disabled? (not (permissions/has-permission? edit-permissions))]
    {:primary? true
     :disabled? disabled?
     :test-id "entities-grid-new"
     :href (when (not disabled?)
             (if new-ui-form?
               (router/get-href (:new urls)
                                {:project-id (:id @(re-frame/subscribe [::subs/current-project]))})
               (broutes/get-url-view (:new urls))))
     :on-click (fn [_e]
                 (when-not disabled?
                   (mixpanel/track "Grid Actions" {"Entity" (name entity)})))
     :icon [icons/mux-plus-circle]
     :class "hide-text"
     :text (get-translation
             (str (name entity) ".new")
             (str "New" " " entity-name))
     :title (get-translation
             (str (name entity) ".new")
             (str "New" " " entity-name))}))

(defn get-entity-filters [entity]
  (let [fields @(re-frame/subscribe [::subs/fields])
        table-id (get-in entities-metadata [entity :table-id])
        project-id @(re-frame/subscribe [::subs/current-project-id])
        autofilters (grid-session-storage/load-autofilters (str project-id "-" table-id))]
    (server/autofilters->json (server/process-autofilters autofilters fields))))

(defn batch-edit-button-data [entity selection current-view state-key has-edit-permissions?]
  (let [{:keys [batch-edit]} (get-in entities-metadata [entity :urls])
        filters (get-entity-filters entity)
        new-batch-modal? @(re-frame/subscribe [::subs/new-batch-edit-modal?])]
    {:disabled? (or (grid.subs/empty-selection? selection) (not has-edit-permissions?))
     :test-id "entities-grid-batch-edit"
     :on-click (let [opts {:entity entity
                           :state-key state-key
                           :path (broutes/get-url-view batch-edit)
                           :selection selection
                           :current-view current-view
                           :optional-params {:filter filters}}]
                 (fn []
                   (mixpanel/track "Grid Actions" {"Action" "Batch edit"})
                   (if new-batch-modal?
                     (let [n (if (= selection :_all)
                               @(re-frame/subscribe [::grid.subs/entities-total-count entity state-key])
                               (count selection))]
                       (when-not (over-batch-limit n)
                         (re-frame/dispatch [::batch-modal-events/open (assoc opts :mode :edit)])))
                     (common-events/batch-navigate-guarded opts))))
     :class "hide-text"
     :icon [icons/mux-pencil]
     :text (get-translation "entity.button.batch-edit" "Batch Edit")
     :title (get-translation "entity.button.batch-edit" "Batch Edit")}))

(defn batch-version-label-button-data [entity selection current-view state-key has-edit-permissions? versioning-can-use-labels?]
  (let [selected-count (if (= selection :_all)
                         @(re-frame/subscribe [::grid.subs/entities-total-count entity state-key])
                         (count selection))
        too-many-selected? (> selected-count max-version-label-batch-selection)
        disabled? (or (grid.subs/empty-selection? selection)
                      (not has-edit-permissions?)
                      (not versioning-can-use-labels?)
                      too-many-selected?)
        tooltip-text (cond
                       too-many-selected? (get-translation "test.versioning.too-many-tests-selected"
                                                           "Too many tests selected")
                       (or (not has-edit-permissions?)
                           (not versioning-can-use-labels?)) (get-translation "test.versioning.cannot-use-labels"
                                                                              "You don't have permission to set labels")
                       :else (get-translation "test.versioning.set-label" "Set Version Label"))]
    {:disabled? disabled?
     :test-id "tests-grid-batch-set-version-label"
     :on-click (fn []
                 (let [test-ids (prepare-full-selection-ids selection current-view)
                       filters (get-entity-filters entity)]
                   (mixpanel/track "Grid Actions" {"Action" "Batch Set Version Label"})
                   (re-frame/dispatch [::batch-version-label/show-label-modal test-ids filters])))
     :class "hide-text"
     :text (get-translation "test.versioning.set-version" "Set Version")
     :icon [icons/mux-versioning]
     :title (get-translation "test.versioning.set-label" "Set Version Label")
     :tooltip tooltip-text}))

(defn dropdown-actions-menu [items-data disabled?]
  [dropdown/dropdown-menu
   (fn [{:keys [toggle!]}]
     [:button.more-button
      (-> {:class (dom/classes-pairs-v :disabled disabled?)
           :disabled disabled?
           :on-click (fn []
                       (when-not disabled?
                         (toggle!)))}
          (dom/add-test-id "entity-index-grid-more-actions"))
      [icons/mux-three-dots]])
     ;; overlay props
   {:placement "bottom-end"}
   [dropdown/dropdown-menu-body
      ;; TODO: replace with class
    {:style {:width "max-content"
             :min-width "233px"}}
    (for [item items-data]
      (when item
        ^{:key (:text item)} [dropdown/dropdown-menu-item item]))]])

(defn grid-buttons [buttons-data]
  [:<>
   (for [button buttons-data]
     (if (and (:disabled? button) (:tooltip button))
       ^{:key (:text button)}
       [tooltip/mux-tooltip
        {}
        (:tooltip button)
        (fn [props]
          [:span props
           [buttons/button (dissoc button :tooltip)]])]
       ^{:key (:text button)}
       [buttons/button button]))])

(defn find-folder-element [^js e]
  (some-> (.-target e)
          (.closest "[data-folder-id]")
          (.querySelector "li")))

(defn filters-tree [grid-state entity state-key]
  (let [module (:module @grid-state)
        on-filter-select (fn [filter user-action?]
                           (when user-action?
                             (re-frame/dispatch [::project-assistant.events/clear-active-ptql]))
                           (re-frame/dispatch [::common-events/set-current-filter entity state-key
                                               ::common-events/fetch-entity-list
                                               grid-state filter user-action?]))
        on-folder-select (partial-dispatch [::common-events/set-current-folder
                                            entity
                                            state-key
                                            ::common-events/fetch-entity-list-by-folder
                                            grid-state])
        user-id (:id @(re-frame/subscribe [::subs/current-user]))
        project-id (:id @(re-frame/subscribe [::subs/current-project]))
        instance-view-mode? (and (= entity :testset) @(re-frame/subscribe [:practitest.frontend.testset2.subs/instance-view-mode?]))
        new-cv-editor? @(re-frame/subscribe [::subs/new-view-editor-enabled?])
        current-folder @(re-frame/subscribe [::grid.subs/current-folder-selected entity state-key])
        all-entity-fields (re-frame/subscribe [::subs/all-entity-fields])
        {:keys [db-entity edit-permissions]} (get entities-metadata entity)]
    (when-not @(re-frame/subscribe [::grid.subs/expanded? entity state-key])
      [:div.filters-view.overlay-parent
       {:on-drag-leave (fn [^js e]
                          (let [folder-li (find-folder-element e)]
                            (when folder-li
                              (.remove (.-classList folder-li) "folder-highlight"))))
        :on-drag-over  (fn [^js e]
                          (.preventDefault e)
                          (set! (.-dataTransfer.dropEffect e) "move")
                          (let [folder-li (find-folder-element e)
                                folder (some-> folder-li (.closest "[data-folder-id]"))
                                folder-id (some-> folder (.getAttribute "data-folder-id"))]
                          (when (and folder-li (not= folder-id (:id current-folder)))
                            (.add (.-classList folder-li) "folder-highlight"))))
        :on-drop       (fn [^js e]
                          (.preventDefault e)
                          (let [dropped-data (js->clj (js/JSON.parse (.getData (.-dataTransfer e) "application/json"))
                                                      :keywordize-keys true)
                                folder-li (find-folder-element e)
                                folder (some-> folder-li (.closest "[data-folder-id]"))
                                folder-id (some-> folder (.getAttribute "data-folder-id"))
                                entity-type (:entity-type dropped-data)]
                            (.remove (.-classList folder-li) "folder-highlight")
                            (when folder-id
                                (re-frame/dispatch [:move-entities-to-folder state-key grid-state folder-id (:rows dropped-data) entity-type]))))}
       [:> FiltersPanel {:scope {:module module :entity db-entity :user-id (str user-id) :project-id (str project-id)}
                         :isDataReady  (boolean @all-entity-fields)
                         :newFilterId @(re-frame/subscribe [::save-filter-modal/new-filter-id])
                         :notifyFilterChanged #(on-filter-select (js->clj %1 :keywordize-keys true) %2)
                         :notifyFolderChanged #(on-folder-select (js->clj %1 :keywordize-keys true) %2)
                         :editView (when new-cv-editor?
                                     (fn [view-id]
                                       (mixpanel/track "Filter Editor" {"Action Name" "Filter Editor Opened"})
                                       (re-frame/dispatch [::view-editor.events/open-edit (js/parseInt view-id 10)])))
                         :createView (when new-cv-editor?
                                       (fn [parent-id filter-type]
                                         (mixpanel/track "Filter Editor" {"Action Name" "Filter Editor Opened"})
                                         (re-frame/dispatch [::view-editor.events/open-create
                                                             {:parent-id (when parent-id (js/parseInt parent-id 10))
                                                              :entity-type module
                                                              :initial-mode (case filter-type
                                                                              "manual" :ptql
                                                                              "auto" :auto-filter
                                                                              nil)}])))
                         :copyAsPtql (when (= @(re-frame/subscribe [::subs/account-license]) "Corporate")
                                       (fn [filter-id auto-filter-values]
                                         (re-frame/dispatch [::common-events/copy-effective-ptql
                                                             filter-id
                                                             (js->clj auto-filter-values)])))
                         :SearchBox  (reagent/reactify-component search-textbox)
                         :settings {:cacheVersion (aget js/window "_pt" "client_version")
                                    :enableSearch true
                                    :maxNumberOfFavorites (aget js/window "_pt" "max_number_of_favorites")
                                    :enableSettings true
                                    :instanceViewMode instance-view-mode?
                                    :enableAddFilters true
                                    :enableFolders (folders/is-folders-enabled module)
                                    :moduleEditPermissions [edit-permissions "runs"]
                                    :resizable false}}]])))

(def panel-config
  {:filters {:default-size 25
             :min-size 15}
   :grid {:default-size 75
          :min-size 60}})

(defn ptql-editor-bar [_current-entity _state-key]
  (let [copied? (reagent/atom false)
        expanded? (reagent/atom false)
        copy! (fn [text]
                ;; XXX: should we use js/navigator.clipboard.writeText instead of dom/text->clipboard here?
                (dom/text->clipboard text)
                (reset! copied? true)
                (js/setTimeout #(reset! copied? false) 1500))]
    (fn [current-entity state-key]
      (let [ptql @(re-frame/subscribe [::grid.subs/ptql current-entity state-key])
            ptql-source @(re-frame/subscribe [::grid.subs/ptql-source current-entity state-key])
            fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
            users @(re-frame/subscribe [::subs/users])
            groups @(re-frame/subscribe [::subs/groups])
            source-entity (get subs/entity-kw->ptql-name current-entity)]
        (when (seq ptql)
          [:div.ptql-debug-bar
           {:class (when (= :assistant ptql-source) "is-from-assistant")}
           [:div.ptql-debug-bar-header
            [:div.ptql-debug-bar-header-left
             [buttons/icon-button
              {:icon (if @expanded? [icons/mux-chevron-down] [icons/mux-chevron-right])
               :tooltip (if @expanded? "Collapse" "Expand")
               :on-click #(swap! expanded? not)}]
             [:span.ptql-debug-bar-label
              (if (= :assistant ptql-source) "ASSISTANT QUERY" "CV QUERY")]
             (when-not @expanded?
               [:span.ptql-debug-bar-preview ptql])]
            [buttons/button
             {:tertiary? true
              :size "small"
              :icon [icons/mux-copy2]
              :disabled? @copied?
              :text (if @copied? "Copied" "Copy")
              :title (if @copied? "Copied to clipboard" "Copy PTQL to clipboard")
              :on-click #(copy! ptql)}]]
           (when @expanded?
             [ptql-editor/editor
              {:value ptql
               :fields-by-entity fields-by-entity
               :source-entity source-entity
               :users users
               :groups groups
               :read-only? true}])])))))

(defn index-content-panel [{:keys [module current-entity state-key entities-grid]}]
  (let [expanded?  @(re-frame/subscribe [::grid.subs/expanded? current-entity state-key])
        grid-state (reagent/atom {:module module :state-key state-key})]
    [:div.grid-page
     [:div.row.module-container
      [:div.col-12
       [:div.top-page-bar
        [top-page-title current-entity state-key]
        [go-to-component current-entity]]]]
     (when @(re-frame/subscribe [::subs/ptql-debug-bar-enabled?])
       [ptql-editor-bar current-entity state-key])
     [:div.filter-tree-and-grid-container
      (if expanded?
        [entities-grid grid-state]
        [panel-group {:auto-save-id "index-content-panel"}
         [panel-resizeable {:default-size (get-in panel-config [:filters :default-size])
                            :min-size (get-in panel-config [:filters :min-size])
                            :class "responsive-tree"}
          [filters-tree grid-state current-entity state-key]]
         [panel-resize-handle]
         [panel-resizeable {:default-size (get-in panel-config [:grid :default-size])
                            :min-size (get-in panel-config [:grid :min-size])}
          [entities-grid grid-state]]])]]))
